import React from 'react';
import { IconButton, StyledEngineProvider, ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import jaLocale from 'date-fns/locale/ja';

// notistackというスナックバーライブラリ
// https://iamhosseindhv.com/notistack
import { SnackbarProvider, closeSnackbar } from 'notistack';

import 'simplebar-react/dist/simplebar.min.css';

import { IntlProvider } from 'react-intl';

import { createTheme } from 'src/theme';
import useSettings from 'src/hooks/useSettings';
import GlobalStyles from 'src/components/GlobalStyles';
import ScrollReset from 'src/components/ScrollReset';
import routes, { renderRoutes } from 'src/routes';
import { TranslationsProvider } from './contexts/TranslationsContext';
import { ColorsProvider } from './contexts/ColorsContext';
import useUser from 'src/hooks/useUser';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';

function App() {
  const { settings } = useSettings();
  const theme = createTheme({
    theme: settings.theme
  });

  const { user } = useUser();
  const lang = user?.language ? user.language : 'ja';

  const onError = (err) => {
    if (err.code === 'MISSING_TRANSLATION') {
      /**
       * 意図的に日本語用の翻訳ファイルを配備しないため、Format.jsが出力する
       * MISSING_TRANSLATIONエラーを無視する
       */
      console.warn(`Missing translation for: ${err.descriptor.id}`);
      return;
    }
    // 他のエラーはデフォルトのエラーハンドリングを行う
    console.error(err);
  };

  return (
    <IntlProvider
      locale={lang}
      messages={settings.messages[lang]}
      onError={onError}
    >
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={jaLocale}
            dateFormats={{ monthAndYear: 'yyyy年 MM月' }}
          >
            <SnackbarProvider
              dense
              maxSnack={3}
              action={(snackbarId) => (
                <IconButton
                  onClick={() => closeSnackbar(snackbarId)}
                  size="small"
                  color="inherit"
                >
                  <HighlightOffIcon fontSize="inherit" />
                </IconButton>
              )}
            >
              <GlobalStyles />
              <ScrollReset />
              <TranslationsProvider>
                <ColorsProvider>
                  <ChunkErrorBoundary>
                    {renderRoutes(routes)}
                  </ChunkErrorBoundary>
                </ColorsProvider>
              </TranslationsProvider>
            </SnackbarProvider>
          </LocalizationProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </IntlProvider>
  );
}

export default App;
